import './index.css'
import React, {useContext, useEffect, useRef, useState} from "react";
import CountDownBar from "./component/CountDownBar/CountDownBar.tsx";
import GridLetterCard from "./component/GridLetterCard/GridLetterCard.tsx";
import Keyboard from "./component/Keyboard/Keyboard.tsx";
import {PageEnum, UGCPageProps} from "../../../../types/IPage.ts";
import {ThemeContext} from "../../../../providers/themeContext/theme-context.tsx";
import useLoading from "../../../../providers/loadingContext/useLoading.ts";
import {useMusicContext} from "../../../../providers/musicProvider/music-player-context.tsx";
import SettingModal from "../../../home/component/SettingModal.tsx";
import PlayTipModal from "../../../home/component/PlayTipsModal.tsx";
import {apiService} from "../../../../common/utils/http/api-service.ts";
import {IUgcGameResponse, IUGCWord} from "../../../../common/utils/http/IResponse.ts";
import debounce from "lodash/debounce";
import UGCGameEndDialog from "./component/UGCGameEndDialog.tsx";
import {ToastContext} from "../../../../components/CustomToast/ToastProvider.tsx";
import {IUgcGameResult} from "../../../../common/utils/http/IParams.ts";
import HintDialog from "../hintDialog/HintDialog.tsx";
import {GAEvents} from "../../../../common/utils/GA4/GAEvents.ts";

// UGC游戏
const UGCGame: React.FC<UGCPageProps> = ({switchPage, nextUGCGame}) => {
    // 主题全局管理器
    const {imgUrls} = useContext(ThemeContext);
    // 提示toast
    const {addToast} = useContext(ToastContext);
    // 加载中弹窗
    const {showLoading, hideLoading} = useLoading();
    // 全局音乐provider
    const musicManager = useMusicContext();
    const targetRef = useRef<HTMLDivElement>(null)
    // 是否显示游戏结束的弹窗提示
    const [showGameEndTip, setShowGameEndTip] = useState<boolean>(false)
    // 是否显示游戏提示
    const [showPlayTip, setShowPlayTip] = useState<boolean>(false)
    // 是否显示hint弹窗
    const [showHintTip, setShowHintTip] = useState<boolean>(false)
    // 是否弹出设置弹窗
    const [showSetting, setShowSetting] = useState<boolean>(false)
    // 已经输入的单词列表
    const [inputWords, setInputWords] = useState<string[]>([])
    // 正确的单词信息
    const [ugcWord, setUgcWord] = useState<IUGCWord | null>(null)
    // 已经输入的单词列表里所有错误的字母，不可重复
    const [wrongLetters, setWrongLetters] = useState<string[]>([])
    // 倒计时的秒数时间
    const [countDownSeconds, setCountDownSeconds] = useState<number>(1800)
    // 游戏状态，0未开始，1开始，2结束【输了】，3结束【赢了】
    const [gameState, setGameState] = useState<number>(0)
    // 游戏弹窗结果
    const [gameOverResult, setGameOverResult] = useState<IUgcGameResponse | null>(null)

    // 在游戏结束弹窗里点击play again
    const handlePlayAgain = () => {
        // GA埋点
        GAEvents.wordMakerResultNextClick()

        nextUGCGame()
    }

    // 进入页面就开始加载正确单词
    useEffect(() => {
        loadUGCWord()

        // GA埋点
        GAEvents.wordMakerPlayGameShow()
    }, []);

    // 加载单词
    const loadUGCWord = debounce(async () => {
        try {
            showLoading("Game preparing...")
            const response = await apiService.getRUGCWordToPlay();
            if (response.code === 200) {
                setUgcWord(response.data)
            } else {
                addToast({
                    type: 'error',
                    message: response.message
                })
                // 返回游戏主页
                switchPage(PageEnum.WORD_MAKER_HOME)
            }
        } finally {
            hideLoading()
        }
    },200)

    // 提交游戏数据
    const sendUGCGameResult = debounce(async (status: boolean, words: string[]) => {
        try {
            const param: IUgcGameResult = {
                wordId: ugcWord?.id ?? '',
                hasWin: status,
                userId: ugcWord?.userId ?? '',
                correctWord: ugcWord?.word ?? '',
                totalWords: JSON.stringify(words) ?? '',
                playTime: countDownSeconds === 1800 ? 1 : status ? 1800 - countDownSeconds : 1800,
                guessCount: words.length,
            }

            const response = await apiService.submitUgcGameResult(param)
            if (response.code === 200) {
                setGameOverResult(response.data)
            } else {
                addToast({
                    type: 'error',
                    message: response.message
                })
            }

        } catch (e) {

            addToast({
                type: 'error',
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                message: e.toString()
            })
        }
    }, 100)


    useEffect(() => {
        // 游戏失败
        if (gameState === 2) {
            sendUGCGameResult(false, inputWords)
            // 延迟1秒显示
            setTimeout(() => {
                musicManager.playFailSound()
                setShowGameEndTip(true)
            }, 3000);
        }

        // 如果游戏胜利，播放胜利音效
        if (gameState === 3) {
            // GA埋点
            setTimeout(() => {
                musicManager.playSuccessSound()
                setShowGameEndTip(true)
            }, 3000);
        }
    }, [gameState]);

    // 设置游戏状态
    const handleGameState = (state: number) => {
        setGameState(state)
    }

    // 向单词列表添加单词
    const addWordToInputList = (word: string) => {
        const newWordList = [word, ...inputWords];
        setInputWords([...newWordList])
        // 每次添加完都判断一次错误单词
        checkWrongLetters(word, newWordList)
    }

    // 判定输入的单词是否正确，并更新错误单词列表
    const checkWrongLetters = (word: string, wordList: string[]) => {
        // 首先判断是否正确，如果正确，游戏结束
        if (ugcWord?.word === word.toLowerCase()) {
            // 赢了
            handleGameState(3)
            // 游戏结束调用接口
            sendUGCGameResult(true, wordList);
            return
        } else {
            // 如果不正确，就扣除3分钟也就是180秒
            const seconds = countDownSeconds;
            if (seconds <= 180) {
                setCountDownSeconds(0)
                // 输了
                handleGameState(2)
            } else {
                setCountDownSeconds(seconds - 180)
            }
        }

        const letters = word.toLowerCase().split('')

        letters.forEach(letter => {
            if (!ugcWord?.word?.includes(letter)) {
                setWrongLetters(prevWrongLetters => [...prevWrongLetters, letter.toLowerCase()]);
            }
        })
    }

    // 在设置中点击游戏提示
    const showPlayTipModal = () => {
        // 关闭设置弹窗
        setShowSetting(false)
        // 显示游戏提示
        setShowPlayTip(true)
    }

    // 返回按钮的点击事件
    const handleBackButton = async () => {
        // 弹出提示

        switchPage(PageEnum.WORD_MAKER_HOME)
    }

    // 胜利界面card-挑战模式的背景图样式
    const successCardStyle = (gameState === 3 || gameState === 2) ? {
        backgroundImage: `url(${imgUrls.dailyWinCardBgUrl})`,
        transition: 'background-image 1s ease-in-out'
    } : {};


    // 关闭结束弹窗，直接返回home
    const closeGameEndModal = () => {
        // GA埋点

        setShowGameEndTip(false)
        switchPage(PageEnum.WORD_MAKER_HOME)
    }

    // 打开how to play提示弹窗
    const openHowToPlayModal = () => {
        setShowPlayTip(true)
    }

    // 打开设置弹窗
    const openSettingModal = () => {
        // GA埋点
        setShowSetting(true)
    }

    return (
        <div ref={targetRef} className="height-100-percent width-100-percent touch-action-none">
            <div className="global-page-background-img"
                 style={{backgroundImage: `url(${imgUrls.gamePageBgIconUrl})`}}/>
            {/*顶部标题栏*/}
            <div className="top-title-container"
                 style={{backgroundImage: `url(${imgUrls.titleBarBgUrl})`}}>
                <img onClick={handleBackButton} className="height-30 width-30 margin-left-13 object-fit-contain"
                     src={imgUrls.goBackIconUrl} alt="pic"/>
                <div
                    className="font-size-20 color-white text-center position-fixed position-left-50-percent transform-rotate-minus-50 my-roboto-medium">
                    User-generated Word
                </div>
                <img onClick={openHowToPlayModal} className="height-30 width-30 object-fit-contain margin-left-auto"
                     src={imgUrls.gameTipIconUrl}
                     alt="pic"/>
                <img onClick={openSettingModal}
                     className="height-30 width-30 object-fit-contain margin-left-8 margin-right-10"
                     src={imgUrls.settingIconUrl}
                     alt="pic"/>
            </div>
            {/* 倒计时进度框 */}
            <CountDownBar gameState={gameState} handleGameState={handleGameState}
                          inputWords={inputWords}
                          countDownSeconds={countDownSeconds}
                          setCountDownSeconds={setCountDownSeconds}/>
            {/*九宫格字母界面card*/}
            <div>
                <div
                    className={`margin-top-30`}
                    style={successCardStyle}
                >
                    {/*游戏九宫格界面*/}
                    <GridLetterCard wordList={inputWords} correctWord={ugcWord?.word ?? ''}
                    />
                </div>
                <Keyboard
                    handleClickHintKey={() => setShowHintTip(true)}
                    word={ugcWord}
                    inputWords={inputWords}
                    gameState={gameState}
                    handleGameState={handleGameState}
                    addWordToInputList={addWordToInputList}
                    wrongLetters={wrongLetters}/>
            </div>
            {/*daily游戏结束时的弹窗*/}
            <UGCGameEndDialog key={"UGCGameEndDialog"} isOpen={showGameEndTip}
                              result={gameOverResult}
                              handlePlayAgain={handlePlayAgain}
                              openHintDialog={()=> setShowHintTip(true)}
                              onClose={closeGameEndModal}/>
            <HintDialog isOpen={showHintTip} onClose={() => setShowHintTip(false)} hintText={ugcWord?.hint}/>
            {/*设置的弹窗*/}
            <SettingModal key="homeSettingModal" isOpen={showSetting} onClose={() => setShowSetting(false)}
                          onShowPlayTip={showPlayTipModal}/>
            {/*游戏提示的弹窗*/}
            <PlayTipModal key="playTipModal" isOpen={showPlayTip} onClose={() => setShowPlayTip(false)}/>
        </div>
    );
};

export default UGCGame;
